import AppComponent from '../base/app-component.ts';
import { apiGet } from '../lib/api-client.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import { formatDate } from '../lib/utils.ts';
import type { DataTable } from './data-table.ts';

type Tipo = 'EDC' | 'ALT';

interface InvioRaw {
  id: number;
  tipo: Tipo;
  created_at: string;
  operatore: string;
  codice_inserimento_statini: string | null;
  totale: number;
  ok: number;
  errori: number;
  dry_run: 0 | 1;
}

interface InviiResponse { invii: InvioRaw[]; totale: number }

interface DettaglioResponse {
  invio: InvioRaw;
  righe: Array<Record<string, unknown>>;
}

interface FiltriAvanzati {
  tipo?: '' | Tipo;
  da?: string;
  a?: string;
}

const PAGE_SIZE = 30;

/** "YYYY-MM-DD HH:MM:SS" → "HH:MM" (vuoto se assente). */
function oraDa(created: string): string {
  const parts = (created || '').split(/[T ]/);
  if (parts.length < 2) return '';
  const hm = parts[1]!.slice(0, 5);
  return /^\d{2}:\d{2}$/.test(hm) ? hm : '';
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

function adaptRow(it: InvioRaw): Record<string, unknown> {
  const ora = oraDa(it.created_at);
  const dataHtml =
    `<span>${escapeHtml(formatDate(it.created_at))}</span>` +
    (ora ? `<span class="text-muted ms-1 small">${ora}</span>` : '');
  return {
    id: it.id,
    tipo: it.tipo,
    created_at: it.created_at,
    createdLabel: dataHtml,
    operatore: it.operatore,
    totale: it.totale,
    ok: it.ok,
    errori: it.errori,
    codice: it.codice_inserimento_statini,
  };
}

function adaptInvio(it: InvioRaw): Record<string, unknown> {
  return {
    id: it.id,
    tipo: it.tipo,
    dataLabel: formatDate(it.created_at),
    oraLabel: oraDa(it.created_at),
    operatore: it.operatore,
    totale: it.totale,
    ok: it.ok,
    errori: it.errori,
    codice: it.codice_inserimento_statini,
    dryRun: it.dry_run === 1,
  };
}

/**
 * wc-statini-inviati — Storico invii a Statini.
 *
 * Wrapper sopra <wc-data-table>: filtro/paginazione/sort client-side.
 * Filtri avanzati in offcanvas (tipo, data). Drill-down dettaglio in modal.
 * Gli invii dry-run NON sono mostrati (sempre `dryRun=false` lato API).
 */
export class StatiniInviati extends AppComponent {
  static override componentName = 'wc-statini-inviati';
  protected override get templateId() { return 'statini-inviati'; }

  private tutte: InvioRaw[] = [];
  private filtrate: InvioRaw[] = [];
  private currentPage = 1;
  private filterText = '';
  private currentSort: string | null = 'createdLabel';
  private currentDir: 'asc' | 'desc' | null = 'desc';
  private filtriAvanzati: FiltriAvanzati = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private offcanvas: any = null;
  private modal: { show(): void; hide(): void } | null = null;

  protected override afterRender(): void {
    const dt = this.dataTable;
    if (!dt) return;

    this.listen(dt, 'dt-filter-change', (e: Event) => {
      this.filterText = ((e as CustomEvent).detail.filter as string).toLowerCase();
      this.currentPage = 1;
      this.applyFilterAndRender();
    });
    this.listen(dt, 'dt-page-change', (e: Event) => {
      this.currentPage = (e as CustomEvent).detail.page as number;
      this.renderPage();
    });
    this.listen(dt, 'dt-sort-change', (e: Event) => {
      const d = (e as CustomEvent).detail as { sort: string | null; dir: 'asc' | 'desc' | null };
      this.currentSort = d.sort;
      this.currentDir = d.dir;
      this.applyFilterAndRender();
    });
    this.listen(dt, 'dt-row-action', (e: Event) => {
      const d = (e as CustomEvent).detail as { action: string; row: InvioRaw };
      if (d.action === 'dettaglio') void this.openDettaglio(Number(d.row.id));
    });

    this.setupFiltri();
    this.setupModal();
    void this.load();
  }

  private setupFiltri(): void {
    const offEl = this.$('[data-offcanvas="filtri"]');
    if (offEl) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.offcanvas = new (globalThis as any).bootstrap.Offcanvas(offEl);
    }
    this.delegate.on('click', '[data-action="apri-filtri"]', () => {
      this.offcanvas?.show();
    });

    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.raccogliFiltriAvanzati();
        this.offcanvas?.hide();
        this.currentPage = 1;
        this.applyFilterAndRender();
      });
    }

    this.delegate.on('click', '[data-action="reset-filtri"], [data-action="reset-filtri-toolbar"]', () => {
      this.resetFiltri();
      this.offcanvas?.hide();
      this.currentPage = 1;
      this.applyFilterAndRender();
    });
  }

  private setupModal(): void {
    const modalEl = this.$('[data-modal="dettaglio-invio"]');
    if (!modalEl) return;
    const bs = (globalThis as unknown as {
      bootstrap: { Modal: new (el: Element) => { show(): void; hide(): void } };
    }).bootstrap;
    this.modal = new bs.Modal(modalEl);
  }

  private raccogliFiltriAvanzati(): void {
    const get = (campo: string): string => {
      const el = this.$(`[data-filtro-val="${campo}"]`) as HTMLInputElement | HTMLSelectElement | null;
      return el?.value.trim() ?? '';
    };
    const tipoVal = get('tipo');
    this.filtriAvanzati = {
      tipo: tipoVal === 'EDC' || tipoVal === 'ALT' ? tipoVal : '',
      da: get('da'),
      a: get('a'),
    };
  }

  private resetFiltri(): void {
    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    form?.reset();
    this.filtriAvanzati = {};
    this.filterText = '';
    const searchInput = this.$('[data-dt-filter-input]') as HTMLInputElement | null;
    if (searchInput) searchInput.value = '';
    this.updateFiltriCount();
  }

  private updateFiltriCount(): void {
    const count = Object.values(this.filtriAvanzati).filter(Boolean).length;
    const badge = this.$('[data-filtri-count]');
    if (badge) {
      badge.textContent = count > 0 ? String(count) : '';
      badge.classList.toggle('d-none', count === 0);
    }
    const resetBtn = this.$('[data-action="reset-filtri-toolbar"]');
    resetBtn?.classList.toggle('d-none', count === 0);
  }

  private get dataTable(): DataTable | null {
    return this.$('wc-data-table') as DataTable | null;
  }

  private async load(): Promise<void> {
    const dt = this.dataTable;
    if (!dt) return;
    dt.setLoading();
    const r = await apiGet<InviiResponse>('statini/invii?dryRun=false');
    if (!r.ok) {
      this.tutte = [];
      this.filtrate = [];
      dt.setError(r.error);
      this.updateCounter();
      Weext.toast(r.error || 'Errore caricamento', { type: 'danger' });
      return;
    }
    this.tutte = r.data.invii;
    this.currentPage = 1;
    this.applyFilterAndRender();
  }

  private applyFilterAndRender(): void {
    const q = this.filterText;
    const { tipo, da, a } = this.filtriAvanzati;
    this.filtrate = this.tutte.filter(it => {
      if (q) {
        const inText = (it.operatore || '').toLowerCase().includes(q)
          || (it.codice_inserimento_statini || '').toLowerCase().includes(q);
        if (!inText) return false;
      }
      if (tipo && it.tipo !== tipo) return false;
      const giorno = (it.created_at || '').slice(0, 10);
      if (da && giorno < da) return false;
      if (a && giorno > a) return false;
      return true;
    });
    this.sortFiltrate();
    this.updateFiltriCount();
    this.renderPage();
  }

  private sortFiltrate(): void {
    const sort = this.currentSort;
    const dir = this.currentDir;
    if (!sort || !dir) return;
    const mult = dir === 'asc' ? 1 : -1;
    // Mappa chiavi UI -> chiavi del dato sottostante
    const fieldFor = (s: string): keyof InvioRaw =>
      s === 'createdLabel' ? 'created_at' : (s as keyof InvioRaw);
    const field = fieldFor(sort);
    this.filtrate.sort((a, b) => {
      const va = a[field];
      const vb = b[field];
      if (va == null && vb == null) return 0;
      if (va == null) return -1 * mult;
      if (vb == null) return 1 * mult;
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return 0;
    });
  }

  private renderPage(): void {
    const dt = this.dataTable;
    if (!dt) return;
    const totale = this.filtrate.length;
    const totalPages = Math.max(1, Math.ceil(totale / PAGE_SIZE));
    if (this.currentPage > totalPages) this.currentPage = totalPages;
    const start = (this.currentPage - 1) * PAGE_SIZE;
    const slice = this.filtrate.slice(start, start + PAGE_SIZE).map(adaptRow);
    dt.setData(slice, {
      page: this.currentPage,
      totalPages,
      count: totale,
      pageSize: PAGE_SIZE,
    });
    this.updateCounter();
  }

  private updateCounter(): void {
    const c = this.$('[data-counter]');
    if (c) c.textContent = `Totale: ${this.filtrate.length}`;
  }

  // --- Drill-down ---

  private renderDettaglio(data: Record<string, unknown>): void {
    const body = this.$('[data-dettaglio-body]');
    if (body) templateParseToElement('statini-inviati-dettaglio', body, data);
  }

  private async openDettaglio(id: number): Promise<void> {
    if (!Number.isInteger(id)) return;
    this.renderDettaglio({ loading: true });
    this.modal?.show();

    const r = await apiGet<DettaglioResponse>(`statini/invii/${id}`);
    if (!r.ok) {
      this.renderDettaglio({ errore: r.error || 'Errore caricamento dettaglio' });
      Weext.toast(r.error || 'Errore caricamento dettaglio', { type: 'danger' });
      return;
    }
    const righe = r.data.righe ?? [];
    this.renderDettaglio({
      invio: adaptInvio(r.data.invio),
      righe,
      isEdc: r.data.invio.tipo === 'EDC',
      empty: righe.length === 0 && r.data.invio.totale === 0,
    });
  }

  protected override onDisconnected(): void {
    this.offcanvas?.dispose();
  }
}
